import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LandingPage from './LandingPage';
import TrendDetailPage from './TrendDetailPage';
import { findTrendBySlug } from './utils/slugs';

// Import assessment component
import InnovationAssessment from './components/InnovationAssessment';

// Import existing pages
import LusideaLandingPage from './components/LusideaLandingPage';
import InnovationManagementPage from './components/InnovationManagementPage';
import FutureThinkingPage from './components/FutureThinkingPage';
import BusinessDesignPage from './components/BusinessDesignPage';
import SelfServiceInnovationPage from './components/SelfServiceInnovationPage';
import LusideaBlog from './components/LusideaBlog';
import LusideaServicesPage from './components/LusideaServicesPage';
import LusideaToolsShowcase from './components/LusideaToolsShowcase';
import BlogPostPage from './components/BlogPostPage';
import LusideaContactPage from './components/LusideaContactPage';
import FutureSignalScanner from './components/FutureSignalScanner';
import InnovationHealthCheck from './components/InnovationHealthCheck';
import CustomerJourneyMapper from './components/CustomerJourneyMapper';
import InnovationCanvasTool from './components/InnovationCanvasTool';
import InnovationOpportunityMapper from './components/InnovationOpportunityMapper';
import InnovationChallengeReframer from './components/InnovationChallengeReframer';
import DesignThinkingWorkshopSimulator from './components/DesignThinkingWorkshopSimulator';
import SixThinkingHatsTool from './components/SixThinkingHatsTool';
import HowMightWeWhatIfGenerator from './components/HowMightWeWhatIfGenerator';

// ==========================================
// NEW: Import Reports System Components
// ==========================================
import ReportsIndex from './components/ReportsIndex';
import ReportDetail from './components/ReportDetail';

// Create search context for global search state
export const SearchContext = createContext();

// Search function to search across trend properties
const searchTrends = (trends, query) => {
  if (!query || query.trim() === '') return trends;
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return trends.filter(trend => {
    return (
      trend.title.toLowerCase().includes(normalizedQuery) ||
      trend.description.toLowerCase().includes(normalizedQuery) ||
      trend.category.toLowerCase().includes(normalizedQuery) ||
      trend.subtitle?.toLowerCase().includes(normalizedQuery) ||
      trend.summary?.toLowerCase().includes(normalizedQuery) ||
      trend.focusArea?.toLowerCase().includes(normalizedQuery) ||
      trend.background?.context?.toLowerCase().includes(normalizedQuery) ||
      trend.opportunity?.toLowerCase().includes(normalizedQuery)
    );
  });
};

// CSS animation styles
const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.5s ease-out forwards;
  }
  
  .animate-fadeInUp {
    animation: fadeInUp 0.5s ease-out forwards;
  }
  
  .animation-delay-100 {
    animation-delay: 0.1s;
  }
  
  .animation-delay-200 {
    animation-delay: 0.2s;
  }
  
  .animation-delay-300 {
    animation-delay: 0.3s;
  }
  
  .animation-delay-400 {
    animation-delay: 0.4s;
  }
  
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

// Trend detail page wrapper component
const TrendDetailWrapper = ({ allTrends, searchQuery, setSearchQuery, searchResults }) => {
  const { slug } = useParams();
  const selectedTrend = findTrendBySlug(allTrends, slug);
  
  if (!selectedTrend) {
    return <Navigate to="/" />;
  }
  
  const relatedTrends = searchQuery && searchResults.length > 0
    ? searchResults.filter(t => t.id !== selectedTrend.id)
    : allTrends.filter(t => t.id !== selectedTrend.id);
  
  return (
    <TrendDetailPage
      trend={selectedTrend}
      relatedTrends={relatedTrends}
    />
  );
};

// Main App Component
const App = () => {
  const [allTrends, setAllTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Fetch trends from JSON file
  useEffect(() => {
    const fetchTrends = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/trends.json');
        if (!response.ok) {
          throw new Error('Failed to fetch trends data');
        }
        const data = await response.json();
        setAllTrends(data);
        setError(null);
      } catch (err) {
        console.error('Error loading trends:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrends();
  }, []);
  
  // Update search results when search query changes
  useEffect(() => {
    const results = searchTrends(allTrends, searchQuery);
    setSearchResults(results);
  }, [searchQuery, allTrends]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading innovation thinking...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto p-6 bg-white rounded-lg shadow-md">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Error Loading Data</h2>
          <p className="text-gray-600 text-center">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-6 w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative">
      <style>{animationStyles}</style>
      
      <SearchContext.Provider value={{ 
        searchQuery, 
        setSearchQuery, 
        searchResults, 
        allTrends 
      }}>
        <Router>
          <Routes>
            {/* Existing routes */}
            <Route path="/" element={<LusideaLandingPage />} />
            
            <Route 
              path="/trends" 
              element={
                <LandingPage 
                  trends={searchQuery ? searchResults : allTrends} 
                />
              } 
            />
            
            <Route 
              path="/trends/:slug" 
              element={
                <TrendDetailWrapper 
                  allTrends={allTrends} 
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  searchResults={searchResults}
                />
              } 
            />
            
            {/* ==========================================
                NEW: Reports System Routes
                ========================================== */}
            
            {/* Main reports listing page */}
            <Route path="/reports" element={<ReportsIndex />} />
            
            {/* Individual report detail pages */}
            <Route path="/reports/:slug" element={<ReportDetail />} />
            
            {/* ==========================================
                Existing routes continue...
                ========================================== */}
            <Route path="/assessment" element={<InnovationAssessment />} />
            <Route path="/services/innovationmanagement" element={<InnovationManagementPage />} />
            <Route path="/services/futurethinking" element={<FutureThinkingPage />} />
            <Route path="/services/businessdesign" element={<BusinessDesignPage />} />
            <Route path="/services/selfserviceinnovation" element={<SelfServiceInnovationPage />} />
            <Route path="/blog" element={<LusideaBlog />} />
            <Route path="/approach" element={<LusideaServicesPage />} />
            <Route path="/tools" element={<LusideaToolsShowcase />} />
            <Route path="/contact" element={<LusideaContactPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/tools/FutureSignalScanner" element={<FutureSignalScanner />} />
            <Route path="/tools/InnovationHealthCheck" element={<InnovationHealthCheck />} />
            <Route path="/tools/CustomerJourneyMapper" element={<CustomerJourneyMapper />} />
            <Route path="/tools/InnovationCanvasTool" element={<InnovationCanvasTool />} />
            <Route path="/tools/InnovationOpportunityMapper" element={<InnovationOpportunityMapper />} />
            <Route path="/tools/InnovationChallengeReframer" element={<InnovationChallengeReframer />} />
            <Route path="/tools/DesignThinking" element={<DesignThinkingWorkshopSimulator />} />
            <Route path="/tools/SixThinkingHats" element={<SixThinkingHatsTool />} />
            <Route path="/tools/QuestionBank" element={<HowMightWeWhatIfGenerator />} />

            {/* Redirect to home page */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </SearchContext.Provider>
    </div>
  );
};

export default App;